// Copyright (c) 2025 GeoVerses. All rights reserved.

import React, { useState } from 'react';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import IconButton from '@mui/material/IconButton';
import FlagIcon from '@mui/icons-material/Flag';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

const FLAG_THRESHOLD = 1;

const PoemReader = ({ poemsInRange }) => {
  const [flagPoem, setFlagPoem] = useState(null);
  const [showFlagged, setShowFlagged] = useState(false);

  const handleFlag = async () => {
    if (!flagPoem) return;
    try {
      const response = await fetch('/flagPoem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ poemId: flagPoem.PoemID })
      });
      if (!response.ok) throw new Error('Flag failed');
    } catch (error) {
      console.log(error);
    }
    setFlagPoem(null);
  };

  const filteredPoems = showFlagged
    ? poemsInRange
    : poemsInRange.filter((poem) => (poem.ReviewFlags || 0) < FLAG_THRESHOLD);

  return (
    <div className="page-container">
      <Typography variant="h5" component="h1" sx={{ my: 2 }}>Nearby Poems</Typography>
      <FormControlLabel
        control={
          <Switch
            checked={showFlagged}
            onChange={(e) => setShowFlagged(e.target.checked)}
            size="small"
          />
        }
        label={<Typography variant="caption">Show flagged poems</Typography>}
        sx={{ alignSelf: 'flex-start', mb: 1 }}
      />
      {filteredPoems.length === 0 ? (
        <Typography variant="body1">No poems in range. Find a nearby poem using the map and move within 50 feet to read it.</Typography>
      ) : (
        <div className="poem-reader-list">
          {filteredPoems.map((poem, index) =>
            <Accordion key={poem.PoemID || (poem.PoemTitle + index)} sx={{ width: '100%' }}>
              <AccordionSummary>
                <div className="poem-text">
                  <strong>{poem.PoemTitle}</strong>
                  <br />    
                  {poem.PoemAuthor}
                </div>
              </AccordionSummary>
              <AccordionDetails>
                <p className="poem-text">
                  {poem.PoemText}
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); setFlagPoem(poem); }}
                    aria-label="flag"
                    color="warning"
                  >
                    <FlagIcon />
                  </IconButton>
                </div>
              </AccordionDetails>
            </Accordion>
          )}
        </div>
      )}

      {/* Flag Confirmation Dialog */}
      <Dialog open={!!flagPoem} onClose={() => setFlagPoem(null)}>
        <DialogTitle>Flag Poem</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Flag this poem as inappropriate or not poetry. Remember art can take many forms and will mean different things to different people so only flag a poem if you are 100% sure it deserves it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFlagPoem(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleFlag}>Flag</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PoemReader;
